/**
 * CommercialProgrammingCalculations.js
 * Funciones puras de cálculo, validación y agregación para Programación Comercial.
 */

export function round(value, decimals = 2) {
  const factor = Math.pow(10, decimals);
  return Math.round((parseFloat(value) || 0) * factor) / factor;
}

export function formatNumber(val, decimals = 2) {
  const num = parseFloat(val);
  if (isNaN(num)) return "0.00";
  return num.toLocaleString("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

export function getIncotermName(id) {
  switch (id) {
    case 1: return "FOB";
    case 2: return "CFR";
    case 3: return "CIF";
    case 4: return "EXW";
    case 5: return "FCA";
    case 6: return "CIP";
    case 7: return "CPT";
    case 8: return "DAP";
    case 9: return "DDP";
    default: return "FOB";
  }
}

/**
 * Calcula volúmenes bidireccionales según la base de ingreso (1: TM, 2: FCL).
 */
export function calculateLineVolume(line) {
  const capacity = parseFloat(line.EpdCapacity) || 0;

  if (line.EpdEntryBase === 2) {
    // Base FCL -> Calcula TM
    const fcl = parseFloat(line.EpdProgrammedFcl) || 0;
    line.EpdProgrammedTons = round(fcl * capacity, 4);
  } else {
    // Base TM -> Calcula FCL
    const tons = parseFloat(line.EpdProgrammedTons) || 0;
    line.EpdProgrammedFcl = capacity > 0 ? round(tons / capacity, 4) : 0;
  }
  return line;
}

/**
 * Calcula precios, diferencial e importes contractuales y FOB equivalente.
 */
export function calculateLineFinancials(line) {
  const priceAgreed = parseFloat(line.EpdPriceAgreed) || 0;
  let priceFobEq = parseFloat(line.EpdPriceFobEquivalent) || 0;

  // Si Incoterm es FOB (1), sincronizar precio FOB Eq con pactado y diferencial = 0
  if (line.TypeIncoterm === 1 || !line.TypeIncoterm) {
    line.TypeIncoterm = 1;
    priceFobEq = priceAgreed;
    line.EpdPriceFobEquivalent = priceAgreed;
    line.EpdPriceDifferential = 0;
  } else {
    // CFR u otros: Diferencial = Precio Pactado - Precio FOB Eq
    line.EpdPriceDifferential = round(priceAgreed - priceFobEq, 4);
  }

  const tons = parseFloat(line.EpdProgrammedTons) || 0;
  line.EpdAmountContract = round(tons * 1000 * priceAgreed, 2);
  line.EpdAmountFobEquivalent = round(tons * 1000 * priceFobEq, 2);
  return line;
}

/**
 * Recalcula completamente una línea comercial (volumen + financieros).
 */
export function recalculateLine(line) {
  calculateLineVolume(line);
  calculateLineFinancials(line);
  return line;
}

/**
 * Valida la consistencia de una línea comercial antes de guardarse.
 */
export function validateLine(line) {
  if (!line.EpdProduct || !String(line.EpdProduct).trim()) {
    return { valid: false, message: "El campo Producto es obligatorio." };
  }
  if (line.EpdCapacity === null || line.EpdCapacity === undefined || isNaN(parseFloat(line.EpdCapacity)) || parseFloat(line.EpdCapacity) <= 0) {
    return { valid: false, message: "Capacidad pendiente: Debe ingresar o seleccionar una capacidad TM/FCL mayor a cero." };
  }
  if (
    (!line.EpdProgrammedTons || parseFloat(line.EpdProgrammedTons) <= 0) &&
    (!line.EpdProgrammedFcl || parseFloat(line.EpdProgrammedFcl) <= 0)
  ) {
    return { valid: false, message: "Debe ingresar un volumen programado (TM o FCL) mayor a cero." };
  }
  if (line.EpdPriceAgreed === null || line.EpdPriceAgreed === undefined || parseFloat(line.EpdPriceAgreed) < 0) {
    return { valid: false, message: "El precio pactado no puede ser negativo." };
  }
  if (!line.EpdCustomerName || !String(line.EpdCustomerName).trim()) {
    return { valid: false, message: "Debe asignar un cliente comercial válido para esta línea." };
  }
  return { valid: true, message: "" };
}

/**
 * Agrupa y consolida la información de clientes (Presupuesto Directo + Asignado de Bolsa + Líneas Activas).
 */
export function computeCustomerCards(budgetCustomers = [], activeLines = []) {
  const map = new Map();

  // 1. Clientes del presupuesto maestro con disponibilidad acumulada
  budgetCustomers.forEach((bc) => {
    const key = bc.PrsCustomerID || bc.CustomerName || "Sin Cliente";
    const directBudgetTons = parseFloat(bc.BudgetTons) || 0;
    const directBudgetFcl = parseFloat(bc.BudgetFcl) || 0;
    const directBudgetFobUsd = parseFloat(bc.BudgetFobUsd) || 0;
    const otherProgTons = parseFloat(bc.OtherProgrammedTons) || 0;
    const otherProgFcl = parseFloat(bc.OtherProgrammedFcl) || 0;
    const otherProgFobUsd = parseFloat(bc.OtherProgrammedFobUsd) || 0;

    const availableTons = bc.AvailableBudgetTons !== undefined && bc.AvailableBudgetTons !== null
      ? parseFloat(bc.AvailableBudgetTons) || 0
      : round(directBudgetTons - otherProgTons, 2);

    const availableFcl = bc.AvailableBudgetFcl !== undefined && bc.AvailableBudgetFcl !== null
      ? parseFloat(bc.AvailableBudgetFcl) || 0
      : round(directBudgetFcl - otherProgFcl, 1);

    map.set(key, {
      customerId: bc.PrsCustomerID,
      customerName: bc.CustomerName || "Sin Cliente",
      budgetTons: directBudgetTons,
      budgetFcl: directBudgetFcl,
      budgetFobUsd: directBudgetFobUsd,
      directBudgetTons,
      directBudgetFcl,
      directBudgetFobUsd,
      allocatedFromBagTons: 0,
      allocatedFromBagFcl: 0,
      allocatedFromBagFobUsd: 0,
      otherProgrammedTons: otherProgTons,
      otherProgrammedFcl: otherProgFcl,
      otherProgrammedFobUsd: otherProgFobUsd,
      availableBudgetTons: availableTons,
      availableBudgetFcl: availableFcl,
      programmedTons: 0,
      programmedFcl: 0,
      programmedFobUsd: 0,
      lineCount: 0,
    });
  });

  // 2. Acumular líneas programadas válidas en esta programación
  activeLines.forEach((l) => {
    const key = l.PrsCustomerID || l.EpdCustomerName || "Sin Cliente";
    if (!map.has(key)) {
      map.set(key, {
        customerId: l.PrsCustomerID,
        customerName: l.EpdCustomerName || "Sin Cliente",
        budgetTons: 0,
        budgetFcl: 0,
        budgetFobUsd: 0,
        directBudgetTons: 0,
        directBudgetFcl: 0,
        directBudgetFobUsd: 0,
        allocatedFromBagTons: 0,
        allocatedFromBagFcl: 0,
        allocatedFromBagFobUsd: 0,
        otherProgrammedTons: 0,
        otherProgrammedFcl: 0,
        otherProgrammedFobUsd: 0,
        availableBudgetTons: 0,
        availableBudgetFcl: 0,
        programmedTons: 0,
        programmedFcl: 0,
        programmedFobUsd: 0,
        lineCount: 0,
      });
    }
    const item = map.get(key);
    const tons = parseFloat(l.EpdProgrammedTons) || 0;
    const fcl = parseFloat(l.EpdProgrammedFcl) || 0;
    const fob = parseFloat(l.EpdAmountFobEquivalent) || 0;

    item.programmedTons += tons;
    item.programmedFcl += fcl;
    item.programmedFobUsd += fob;
    item.lineCount += 1;

    // Si la línea proviene de la bolsa "Sin Cliente", sumar a asignado de bolsa
    if (l.PrsCustomerOriginID === "Sin Cliente" || l.PrsCustomerOriginID === "SIN CLIENTE" || (l.EpdCustomerOriginName && l.EpdCustomerOriginName.includes("Sin Cliente"))) {
      item.allocatedFromBagTons += tons;
      item.allocatedFromBagFcl += fcl;
      item.allocatedFromBagFobUsd += fob;
    }
  });

  // 3. Determinar saldos operativos, base disponible, variación y estados visuales
  return Array.from(map.values()).map((c) => {
    const directTons = round(c.directBudgetTons || c.budgetTons, 2);
    const directFcl = round(c.directBudgetFcl || c.budgetFcl, 1);
    const directFob = round(c.directBudgetFobUsd || c.budgetFobUsd, 2);

    const bagTons = round(c.allocatedFromBagTons, 2);
    const bagFcl = round(c.allocatedFromBagFcl, 1);
    const bagFob = round(c.allocatedFromBagFobUsd, 2);

    // Base Disponible = Presupuesto Directo + Asignado desde Bolsa
    const availableBaseTons = round(directTons + bagTons, 2);
    const availableBaseFcl = round(directFcl + bagFcl, 1);
    const availableBaseFobUsd = round(directFob + bagFob, 2);

    // Saldo Operativo = Base Disponible - Total Programado
    const operatingBalanceTons = round(availableBaseTons - c.otherProgrammedTons - c.programmedTons, 2);
    const operatingBalanceFcl = round(availableBaseFcl - c.otherProgrammedFcl - c.programmedFcl, 1);
    const operatingBalanceFobUsd = round(availableBaseFobUsd - c.otherProgrammedFobUsd - c.programmedFobUsd, 2);

    // Variación vs Presupuesto Original Directo
    const varianceTons = round((c.otherProgrammedTons + c.programmedTons) - directTons, 2);
    const varianceFcl = round((c.otherProgrammedFcl + c.programmedFcl) - directFcl, 1);
    const varianceFobUsd = round((c.otherProgrammedFobUsd + c.programmedFobUsd) - directFob, 2);

    const totalProgrammedTons = round(c.otherProgrammedTons + c.programmedTons, 2);

    let statusKey = "unprogrammed";
    let statusLabel = "Sin Prog.";
    let statusBadgeColor = "grey lighten-3";
    let statusTextColor = "grey darken-3";
    let progressBarColor = "grey lighten-1";

    // Si tiene asignación de bolsa y saldo operativo >= 0 -> Asignado desde bolsa (púrpura/indigo)
    if (directTons === 0 && bagTons > 0 && operatingBalanceTons >= -0.001) {
      statusKey = "bag_allocated";
      statusLabel = "Asignado desde bolsa";
      statusBadgeColor = "purple lighten-5";
      statusTextColor = "deep-purple darken-3";
      progressBarColor = "deep-purple";
    } else if (operatingBalanceTons < -0.001) {
      statusKey = "over";
      statusLabel = "Sobre Base";
      statusBadgeColor = "red lighten-5";
      statusTextColor = "red darken-4";
      progressBarColor = "red darken-2";
    } else if (totalProgrammedTons > 0 && Math.abs(operatingBalanceTons) <= 0.001) {
      statusKey = "complete";
      statusLabel = "Listo";
      statusBadgeColor = "green lighten-5";
      statusTextColor = "green darken-4";
      progressBarColor = "green";
    } else if (totalProgrammedTons > 0 && operatingBalanceTons > 0.001) {
      statusKey = "partial";
      statusLabel = "Parcial";
      statusBadgeColor = "blue lighten-5";
      statusTextColor = "blue darken-4";
      progressBarColor = "blue";
    }

    const progressPercent = availableBaseTons > 0
      ? Math.min(Math.round((totalProgrammedTons / availableBaseTons) * 100), 100)
      : (totalProgrammedTons > 0 ? 100 : 0);

    return {
      ...c,
      directBudgetTons: directTons,
      directBudgetFcl: directFcl,
      directBudgetFobUsd: directFob,
      allocatedFromBagTons: bagTons,
      allocatedFromBagFcl: bagFcl,
      allocatedFromBagFobUsd: bagFob,
      availableBaseTons,
      availableBaseFcl,
      availableBaseFobUsd,
      balanceTons: operatingBalanceTons,
      balanceFcl: operatingBalanceFcl,
      balanceFobUsd: operatingBalanceFobUsd,
      operatingBalanceTons,
      operatingBalanceFcl,
      operatingBalanceFobUsd,
      varianceTons,
      varianceFcl,
      varianceFobUsd,
      totalProgrammedTons,
      statusKey,
      statusLabel,
      statusBadgeColor,
      statusTextColor,
      progressBarColor,
      progressPercent,
    };
  });
}

/**
 * Calcula los totales consolidados de la programación versus presupuesto.
 */
export function computeProgramSummary(budgetBaseline = {}, activeLines = []) {
  let progTons = 0, progFcl = 0, progFob = 0, progContract = 0;

  activeLines.forEach((l) => {
    progTons += parseFloat(l.EpdProgrammedTons) || 0;
    progFcl += parseFloat(l.EpdProgrammedFcl) || 0;
    progFob += parseFloat(l.EpdAmountFobEquivalent) || 0;
    progContract += parseFloat(l.EpdAmountContract) || 0;
  });

  const pTons = parseFloat(budgetBaseline.TotalTons) || 0;
  const pFcl = parseFloat(budgetBaseline.TotalFcl) || 0;
  const pFob = parseFloat(budgetBaseline.TotalFobUsd) || 0;

  return {
    ProgrammedTons: round(progTons, 2),
    ProgrammedFcl: round(progFcl, 1),
    ProgrammedContractUsd: round(progContract, 2),
    ProgrammedFobUsd: round(progFob, 2),
    BalanceTons: round(pTons - progTons, 2),
    BalanceFcl: round(pFcl - progFcl, 1),
    BalanceFobUsd: round(pFob - progFob, 2),
  };
}
